import { useState, useEffect } from "react";
import {
  Container,
  Select,
  Group,
  Button,
  Table,
  Checkbox,
  Badge,
  Text,
  TextInput,
  Alert,
  Center,
  Loader,
  Stack,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconArrowRight } from "@tabler/icons-react";
import { getFlows, getEnvironmentDiff, promoteEnvironment } from "../api";

export default function EnvironmentPromote() {
  const [flows, setFlows] = useState(null);
  const [environments, setEnvironments] = useState([]);
  const [source, setSource] = useState(null);
  const [target, setTarget] = useState(null);
  const [diff, setDiff] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [promoting, setPromoting] = useState(false);

  useEffect(() => {
    getFlows().then((data) => {
      setFlows(data);
      // Extract unique environment names from all flows
      const envSet = new Set();
      data.forEach((flow) => {
        if (flow.activeVersions) {
          Object.keys(flow.activeVersions).forEach((env) => envSet.add(env));
        }
      });
      setEnvironments([...envSet].sort());
    });
  }, []);

  const fetchDiff = async () => {
    if (!source || !target) return;
    setLoading(true);
    try {
      const data = await getEnvironmentDiff(source, target);
      setDiff(data);
      // Pre-select changed flows
      const changedIds = new Set(
        data
          .filter((d) => d.sourceVersionId !== d.targetVersionId)
          .map((d) => d.flowId)
      );
      setSelected(changedIds);
    } finally {
      setLoading(false);
    }
  };

  const handlePromote = async () => {
    if (selected.size === 0) return;
    setPromoting(true);
    try {
      const result = await promoteEnvironment({
        sourceEnvironment: source,
        targetEnvironment: target,
        flowIds: [...selected],
        comment: comment || null,
      });
      notifications.show({
        title: "Promotion complete",
        message: `${result.promotedCount} flow(s) promoted to "${target}"`,
        color: "green",
      });
      if (result.failures?.length > 0) {
        notifications.show({
          title: "Some flows failed",
          message: result.failures.join("\n"),
          color: "orange",
          autoClose: false,
        });
      }
      // Promotion-time validation warnings (warn-and-allow): e.g. a routing rule that is
      // not enabled for the target environment — the promotion stands, but requests would
      // fail at runtime until the config is fixed.
      if (result.warnings?.length > 0) {
        notifications.show({
          title: "Promotion warnings",
          message: result.warnings.join("\n"),
          color: "yellow",
          autoClose: false,
        });
      }
      // Re-fetch diff to show updated state
      await fetchDiff();
    } finally {
      setPromoting(false);
    }
  };

  const toggleAll = () => {
    if (!diff) return;
    const changeable = diff.filter(
      (d) => d.sourceVersionId !== d.targetVersionId
    );
    if (selected.size === changeable.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(changeable.map((d) => d.flowId)));
    }
  };

  const toggleOne = (flowId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(flowId)) next.delete(flowId);
      else next.add(flowId);
      return next;
    });
  };

  if (!flows)
    return (
      <Center mt="xl">
        <Loader size="xl" />
      </Center>
    );

  const envOptions = environments.map((e) => ({ value: e, label: e }));
  const changeableCount = diff
    ? diff.filter((d) => d.sourceVersionId !== d.targetVersionId).length
    : 0;

  return (
    <Container size="lg" px={0}>
      <Text fw={700} size="lg" mb="md">
        Promote Environment
      </Text>

      <Group mb="md" align="end">
        <Select
          label="Source"
          placeholder="Select source"
          data={envOptions}
          value={source}
          onChange={setSource}
          style={{ minWidth: 180 }}
        />
        <IconArrowRight size={20} style={{ marginBottom: 8 }} />
        <Select
          label="Target"
          placeholder="Select target"
          data={envOptions}
          value={target}
          onChange={setTarget}
          style={{ minWidth: 180 }}
        />
        <Button
          onClick={fetchDiff}
          loading={loading}
          disabled={!source || !target || source === target}
        >
          Compare
        </Button>
      </Group>

      {source === target && source != null && (
        <Alert color="yellow" mb="md">
          Source and target must be different environments.
        </Alert>
      )}

      {diff && (
        <Stack gap="md">
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={40}>
                  <Checkbox
                    checked={
                      changeableCount > 0 && selected.size === changeableCount
                    }
                    indeterminate={
                      selected.size > 0 && selected.size < changeableCount
                    }
                    onChange={toggleAll}
                    disabled={changeableCount === 0}
                  />
                </Table.Th>
                <Table.Th>Flow</Table.Th>
                <Table.Th>Source Version</Table.Th>
                <Table.Th>Target Version</Table.Th>
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {diff.map((item) => {
                const isDifferent =
                  item.sourceVersionId !== item.targetVersionId;
                const isNew = isDifferent && !item.targetVersionId;
                return (
                  <Table.Tr key={item.flowId}>
                    <Table.Td>
                      <Checkbox
                        checked={selected.has(item.flowId)}
                        onChange={() => toggleOne(item.flowId)}
                        disabled={!isDifferent}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {item.flowTitle || item.flowName}
                      </Text>
                      {item.flowName && item.flowTitle && (
                        <Text size="xs" c="dimmed">
                          {item.flowName}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" ff="monospace">
                        {item.sourceVersionId?.slice(-6)}
                      </Text>
                      {item.sourceVersionComment && (
                        <Text size="xs" c="dimmed">
                          {item.sourceVersionComment}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" ff="monospace">
                        {item.targetVersionId?.slice(-6) ?? "—"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {isNew ? (
                        <Badge color="blue" variant="light">
                          New
                        </Badge>
                      ) : isDifferent ? (
                        <Badge color="yellow" variant="light">
                          Changed
                        </Badge>
                      ) : (
                        <Badge color="gray" variant="light">
                          Same
                        </Badge>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>

          {diff.length === 0 && (
            <Text c="dimmed" ta="center">
              No flows found in the source environment.
            </Text>
          )}

          {changeableCount > 0 && (
            <Group>
              <TextInput
                placeholder="Optional promote comment"
                value={comment}
                onChange={(e) => setComment(e.currentTarget.value)}
                style={{ flex: 1 }}
              />
              <Button
                onClick={handlePromote}
                loading={promoting}
                disabled={selected.size === 0}
              >
                Promote {selected.size} flow(s)
              </Button>
            </Group>
          )}

          {changeableCount === 0 && diff.length > 0 && (
            <Alert color="green">
              All flows are already in sync between these environments.
            </Alert>
          )}
        </Stack>
      )}
    </Container>
  );
}
